
import React from 'react';

const GameGuide = () => {
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
        <div className="p-6 sm:p-10">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
            Comprehensive Guide to Pokemon Clicker 2
          </h2>
          
          <div className="prose prose-lg max-w-none dark:prose-invert">
            <h3>Getting Started with Pokemon Clicker 2</h3>
            
            <p>
              When you first launch <strong>Pokemon Clicker 2</strong>, you'll be greeted with a simple interface featuring a Pokemon (usually a starter like Bulbasaur, Charmander, or Squirtle) and a counter showing your current points. The core mechanics are straightforward—click on the Pokemon to earn points—but mastering the game requires strategy and understanding of its various systems.
            </p>
            
            <h4>Basic Controls</h4>
            <ul>
              <li><strong>Left-click</strong> on the Pokemon to earn points</li>
              <li><strong>Navigate menus</strong> using mouse clicks</li>
              <li><strong>Purchase upgrades</strong> from the shop using earned currency</li>
              <li><strong>Check your Pokedex</strong> to track collection progress</li>
              <li><strong>Save</strong> happens automatically as you play</li>
            </ul>
            
            <h3>Currency System</h3>
            
            <p>
              Pokemon Clicker 2 features a dual currency system:
            </p>
            
            <ul>
              <li><strong>PokeDollars (P$)</strong>: The main currency earned through clicking and automatic generation. Used for most common upgrades.</li>
              <li><strong>PokeGems</strong>: Premium currency earned more slowly through achievements and special events. Used for rare upgrades and boosts.</li>
            </ul>
            
            <h3>Upgrade Categories</h3>
            
            <p>
              Effective progression in Pokemon Clicker 2 requires strategic investment in various upgrade categories:
            </p>
            
            <h4>Click Power Upgrades</h4>
            <p>
              These upgrades increase the amount of PokeDollars earned per click. Early in the game, these should be your priority as they provide the most immediate boost to your earning potential.
            </p>
            
            <h4>Auto-Clickers</h4>
            <p>
              Auto-clickers generate PokeDollars automatically without requiring manual clicks. They come in various forms, such as:
            </p>
            <ul>
              <li><strong>Poke Assistants</strong>: Basic auto-clickers that generate a small number of clicks per second</li>
              <li><strong>Pokemon Centers</strong>: Generate more clicks but cost significantly more</li>
              <li><strong>Regional Professors</strong>: High-tier auto-clickers with substantial output</li>
            </ul>
            
            <h4>Catch Rate Upgrades</h4>
            <p>
              These upgrades increase your chances of encountering and capturing rare Pokemon, which often provide special bonuses to your clicking power or auto-generation rate.
            </p>
            
            <h4>Evolution Upgrades</h4>
            <p>
              Evolution upgrades allow your Pokemon to evolve into more powerful forms, significantly boosting your clicking power and unlocking new features.
            </p>
            
            <h3>Pokemon Collection Strategy</h3>
            
            <p>
              Building an effective Pokemon collection is central to progressing in Pokemon Clicker 2:
            </p>
            
            <h4>Starter Pokemon</h4>
            <p>
              Your initial Pokemon will be your primary clicking target. Focus on upgrading and evolving it first before diversifying your collection.
            </p>
            
            <h4>Type Coverage</h4>
            <p>
              Different Pokemon types provide various bonuses. For example:
            </p>
            <ul>
              <li><strong>Fire types</strong> might boost critical click chances</li>
              <li><strong>Water types</strong> could increase auto-generation rates</li>
              <li><strong>Electric types</strong> might improve special event rewards</li>
            </ul>
            
            <h4>Legendary Pokemon</h4>
            <p>
              Legendary Pokemon are extremely rare but provide substantial bonuses when caught. They often appear during special events or after reaching certain milestones in your Pokedex completion.
            </p>
            
            <h3>Advanced Techniques</h3>
            
            <h4>Prestige System</h4>
            <p>
              After reaching a certain point in the game, you'll unlock the ability to "prestige" or reset your progress in exchange for permanent bonuses. This is a common idle game mechanic that extends gameplay and allows for faster progression on subsequent runs.
            </p>
            
            <h4>Gym Battles</h4>
            <p>
              Challenging gym leaders becomes available as you progress. These battles require specific Pokemon types or power levels and reward you with badges that provide permanent bonuses.
            </p>
            
            <h4>Achievements</h4>
            <p>
              Complete various achievements to earn PokeGems and unlock special features. Achievements range from simple tasks like clicking a certain number of times to complex challenges like completing specific Pokemon type collections.
            </p>
            
            <h3>Tips for New Players</h3>
            
            <ul>
              <li>Focus on click power upgrades early in the game</li>
              <li>Invest in auto-clickers once clicking becomes tedious</li>
              <li>Complete achievements to earn PokeGems without spending real money</li>
              <li>Evolve your starter Pokemon as soon as possible</li>
              <li>Participate in special events to catch rare Pokemon</li>
              <li>Balance your upgrade purchases between immediate benefits and long-term gains</li>
              <li>Check the Pokedex regularly to identify which Pokemon you still need to catch</li>
            </ul>
            
            <p>
              With this guide, you're well-equipped to begin your journey in Pokemon Clicker 2. Remember that like most idle games, progress requires patience and strategic planning. Enjoy the process of building your Pokemon collection and watching your clicking power grow exponentially over time!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameGuide;
